import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Calendar } from "../../../../assets/icons/Calendar";
import { DateRangePicker } from "../../../../components/DateRangePicker";
import { OptionButton, OptionsBar } from "../../../../components/OptionsBar";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { DateFilterOptionType } from "../MeasurementModal/components/CustomOptionsBar";
import {
  DatepickerContainer,
  OptionsBarContainer,
} from "../MeasurementModal/components/CustomOptionsBar/CustomOptionsBar.styles";
import { dateFilterOptions } from "../MeasurementModal/components/CustomOptionsBar/dateFilterOptions";

type EcgOptionsBarProps = {
  selectedFilterDate: DateFilterOptionType;
  onChange: (selectedFilterDate: DateFilterOptionType) => void;
};

const CUSTOM_DATE_FILTER = "custom";

// Date-range bar for the ECG table — mirrors the Sleep/Fitbit options bar
// (Custom + presets) but without the graph/table toggle (ECG is always a table).
export function EcgOptionsBar({
  selectedFilterDate,
  onChange,
}: EcgOptionsBarProps) {
  const { t } = useTranslation();

  const [calendarOpen, setCalendarOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const calendarButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarOpen &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        calendarButtonRef.current &&
        !calendarButtonRef.current.contains(event.target as Node)
      ) {
        setCalendarOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () =>
      document.removeEventListener("click", handleClickOutside, true);
  }, [calendarOpen]);

  const handleCustomDateChange = (dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    if (dateRange.startDate && dateRange.endDate) {
      setCalendarOpen(false);
      onChange({
        id: CUSTOM_DATE_FILTER,
        startDate: dayjs(dateRange.startDate),
        endDate: dayjs(dateRange.endDate).endOf("day"),
      });
    }
  };

  return (
    <OptionsBarContainer>
      <OptionsBar>
        <div
          style={{ backgroundColor: "white", borderRadius: "8px 0 0 8px" }}
          ref={calendarButtonRef}
        >
          <OptionButton
            key="ecg_customDate"
            text={t("patientPage_ecg_custom")}
            icon={<Calendar />}
            selected={selectedFilterDate.id === CUSTOM_DATE_FILTER}
            onClick={() => {
              mixpanelActions.track("User Action: CustomDateFilter");
              setCalendarOpen((prevState) => !prevState);
            }}
          />
        </div>

        {dateFilterOptions.map((dateFilterOption, index) => (
          <OptionButton
            key={`ecg_${dateFilterOption.id}_${index}`}
            text={t(dateFilterOption.labelKey)}
            selected={dateFilterOption.id === selectedFilterDate.id}
            onClick={() => {
              mixpanelActions.track(
                `User Action: Changed Date Range ${dateFilterOption.id}`
              );
              const currentDate = dayjs();
              onChange({
                id: dateFilterOption.id,
                startDate: dateFilterOption.value(currentDate),
                endDate: currentDate,
              });
            }}
          />
        ))}
      </OptionsBar>

      {calendarOpen && (
        <DatepickerContainer ref={wrapperRef}>
          <DateRangePicker
            initialDateRange={{
              startDate: selectedFilterDate.startDate.toDate(),
              endDate: selectedFilterDate.endDate.endOf("day").toDate(),
            }}
            onDateChange={handleCustomDateChange}
          />
        </DatepickerContainer>
      )}
    </OptionsBarContainer>
  );
}
